import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import EmptyState from "@/components/feature/EmptyState";
import { comicCategories } from "@/mocks/categories";
import { getCategoryById, getComicsByCategory } from "@/utils/comic";

const statusOptions = ["全部", "连载中", "已完结"];
const sortOptions = [
  { id: "updated", label: "最新更新" },
  { id: "popular", label: "人气最高" },
  { id: "rating", label: "评分最高" },
  { id: "alpha", label: "字母排序" },
];

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);
  const [status, setStatus] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [visible, setVisible] = useState(8);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setVisible(8);
    setStatus("全部");
    setSort("updated");
  }, [categoryId]);

  const filtered = useMemo(() => {
    const base = categoryId ? getComicsByCategory(categoryId) : [];
    const byStatus =
      status === "全部" ? base : base.filter((c) => c.status === status);
    const sorted = [...byStatus];
    if (sort === "updated") sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    if (sort === "popular") sorted.sort((a, b) => b.popularity - a.popularity);
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    if (sort === "alpha")
      sorted.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
    return sorted;
  }, [categoryId, status, sort]);

  const resetFilters = () => {
    setStatus("全部");
    setSort("updated");
    setVisible(8);
  };

  if (!category) {
    return (
      <div className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到该分类"
          description="你访问的分类可能已调整或不存在，返回首页继续探索其他漫画题材。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold"
            >
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-8 bg-background-100">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "漫画分类", to: "/" },
            { label: category.name },
          ]}
        />
        <div className="mt-5 flex items-center gap-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-lg bg-primary-500">
            <i className={`${category.icon} text-2xl text-background-50`}></i>
          </span>
          <div>
            <h1 className="text-2xl md:text-3xl font-label font-bold text-foreground-950">
              {category.name}漫画
            </h1>
            <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-xs font-label font-bold text-foreground-500 mr-1">题材</span>
          {comicCategories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors ${
                cat.id === category.id
                  ? "bg-primary-500 text-background-50 font-bold"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-background-200">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
              {statusOptions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setStatus(item);
                    setVisible(8);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer ${
                    status === item
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="md:hidden inline-flex items-center gap-1 h-9 px-3 rounded-full bg-background-100 text-xs font-label font-bold text-foreground-700"
            >
              <i className="ri-equalizer-line"></i>
              排序
            </button>
          </div>

          <div
            className={`${filterOpen ? "flex" : "hidden"} md:flex flex-wrap items-center gap-2`}
          >
            <span className="text-xs font-label font-bold text-foreground-500">排序</span>
            {sortOptions.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSort(item.id)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                  sort === item.id
                    ? "bg-secondary-500 text-background-50 font-bold"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-sm text-foreground-600">
          共找到 <strong className="text-primary-600">{filtered.length}</strong> 部作品
        </p>

        <div className="mt-4">
          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="未找到符合条件的漫画"
              description="换个筛选条件试试，或者重置后查看该分类下的全部作品。"
              action={
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              }
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {filtered.slice(0, visible).map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
              {visible < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + 8)}
                    className="inline-flex items-center gap-2 h-11 px-8 rounded-md border border-background-300 text-foreground-800 text-sm font-label font-bold hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-circle-line"></i>
                    加载更多（剩余 {filtered.length - visible} 部）
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}